import type {
  HrCandidate,
  HrFilters,
  HrPerson,
  HrQualification,
  HrRequisition,
} from '../../types/hr-dashboard.types';

export const STANDARD_DAY_HOURS = 7.6;
export const EXCESSIVE_ANNUAL_HOURS = 304;
export const WATCH_ANNUAL_HOURS = 152;
export const WIDE_SPAN = 15;

export const SITE_LEVELS = [
  'Field Worker',
  'Leading Hand',
  'Yardsman',
  'Supervisor',
  'Yard Supervisor',
  'Team Lead',
];

export const PIPELINE_STATUSES = [
  'SHORTLISTED',
  'PHONE_SCREEN',
  'INTERVIEW',
  'PRE_EMPLOYMENT_COMMENCED',
  'PRE_EMPLOYMENT_COMPLETED',
  'DRAFT_OFFER',
  'OFFER_SENT',
  'OFFER_ACCEPTED',
  'HIRING_COMMENCED',
];

export const CORE_TICKETS: { label: string; pattern: RegExp }[] = [
  { label: 'Whitecard', pattern: /whitecard|general construction/i },
  { label: 'Scaffold Basic', pattern: /scaffolder basic/i },
  { label: 'Scaffold Interm.', pattern: /scaffolder intermediate/i },
  { label: 'Scaffold Adv.', pattern: /scaffolder advanced/i },
  { label: 'Working at Heights', pattern: /working at heights/i },
  { label: 'Forklift', pattern: /forklift/i },
  { label: 'Silica', pattern: /silica/i },
  { label: 'Asbestos', pattern: /asbestos/i },
  { label: 'CoR (TLIF0009)', pattern: /TLIF0009|chain of resp/i },
];

export const EMPTY_FILTERS: HrFilters = {
  dept: [],
  region: [],
  emp: [],
  level: [],
};

export const FILTER_LABELS: Record<keyof HrFilters, string> = {
  dept: 'Department',
  region: 'Region',
  emp: 'Employment type',
  level: 'Role level',
};

export const formatNumber = (value: number | null | undefined, digits = 0): string =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : new Intl.NumberFormat('en-AU', {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits > 0 && Number.isInteger(value) ? 0 : 0,
      }).format(value);

export const percent = (part: number, total: number): number =>
  total > 0 ? Math.round((part / total) * 100) : 0;

export const daysBetween = (from: string | null, to: string | null): number | null => {
  if (!from || !to) return null;
  const start = new Date(`${from.slice(0, 10)}T00:00:00`);
  const end = new Date(`${to.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.round((end.getTime() - start.getTime()) / 864e5);
};

export const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
};

export const countBy = <T,>(rows: T[], getKey: (row: T) => string | null | undefined) => {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = getKey(row) || 'Not recorded';
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

export const sortCountsDesc = (counts: Map<string, number>) =>
  [...counts.entries()].sort((a, b) => b[1] - a[1]);

const matchesAny = (selected: string[], value: string | null | undefined) =>
  selected.length === 0 || selected.includes(value || 'Not recorded');

export const filterPeople = (people: HrPerson[], filters: HrFilters): HrPerson[] =>
  people.filter(
    (person) =>
      matchesAny(filters.dept, person.dept) &&
      matchesAny(filters.region, person.region) &&
      matchesAny(filters.emp, person.emp) &&
      matchesAny(filters.level, person.level)
  );

export const filterRequisitions = (
  requisitions: HrRequisition[],
  filters: HrFilters
): HrRequisition[] =>
  requisitions.filter(
    (requisition) =>
      matchesAny(filters.dept, requisition.dept) &&
      matchesAny(filters.region, requisition.region) &&
      matchesAny(filters.level, requisition.level)
  );

export const isSiteLevel = (level: string) => SITE_LEVELS.includes(level);

export const holdsWhitecard = (qualifications: HrQualification[], personId: number) =>
  qualifications.some(
    (qualification) =>
      qualification.personId === personId &&
      CORE_TICKETS[0].pattern.test(qualification.name)
  );

export const isPipelineCandidate = (candidate: HrCandidate) =>
  PIPELINE_STATUSES.includes(candidate.status);

export const offerAwaitingReply = (candidate: HrCandidate) =>
  candidate.status === 'OFFER_SENT';

export const isOfferAccepted = (candidate: HrCandidate) =>
  candidate.status === 'OFFER_ACCEPTED' || candidate.status === 'HIRED';

export const last12Months = (asOf: string) => {
  const base = new Date(`${asOf.slice(0, 10)}T00:00:00`);
  const months: { key: string; label: string; full: string }[] = [];
  for (let offset = 11; offset >= 0; offset -= 1) {
    const date = new Date(base.getFullYear(), base.getMonth() - offset, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    months.push({
      key,
      label: date.toLocaleDateString('en-AU', { month: 'short' }),
      full: date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }),
    });
  }
  return months;
};

export const next12Months = (asOf: string) => {
  const base = new Date(`${asOf.slice(0, 10)}T00:00:00`);
  const months: { key: string; label: string; full: string }[] = [];
  for (let offset = 0; offset < 12; offset += 1) {
    const date = new Date(base.getFullYear(), base.getMonth() + offset, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    months.push({
      key,
      label: date.toLocaleDateString('en-AU', { month: 'short' }),
      full: date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }),
    });
  }
  return months;
};

export const filterSummary = (filters: HrFilters): string =>
  (Object.keys(FILTER_LABELS) as (keyof HrFilters)[])
    .flatMap((key) => filters[key])
    .join(', ');

export const exportFilename = (view: string, filters: HrFilters, asOf: string, ext: string) => {
  const filterTag = filterSummary(filters);
  const date = asOf.slice(0, 10);
  const safeView = view.replace(/[\\/:*?"<>|]/g, ' ').trim();
  const safeFilters = filterTag ? ` (${filterTag.replace(/[\\/:*?"<>|]/g, ' ')})` : '';
  return `Idarah HR - ${safeView}${safeFilters} - ${date}.${ext}`;
};

/* ---------------------------------------------------------------- status */

export const requisitionStatusTone = (status: string) => {
  switch (status) {
    case 'COMPLETE':
      return 'success';
    case 'IN_PROGRESS':
    case 'OPEN':
      return 'warning';
    default:
      return 'muted';
  }
};

export const qualificationStatusTone = (status: string) => {
  switch (status) {
    case 'Expired':
      return 'danger';
    case 'Due ≤30d':
      return 'serious';
    case 'Due 31–90d':
      return 'warning';
    case 'Current':
      return 'success';
    default:
      return 'muted';
  }
};

export const signoffStatusTone = (status: string) => {
  if (status === 'Completed') return 'success';
  if (status === 'Overdue') return 'danger';
  return 'warning';
};

export const trainingPlanStatusTone = (status: string) => {
  if (status === 'Completed') return 'success';
  if (status === 'In Progress') return 'warning';
  return 'muted';
};

export const coverageTone = (value: number) => {
  if (value >= 80) return 'success';
  if (value >= 40) return 'warning';
  return 'danger';
};

export const leaveRiskTone = (annual: number | null, personal: number | null) => {
  if (annual === null && personal === null) return 'muted';
  if (annual !== null && annual < 0) return 'serious';
  if (annual !== null && annual >= EXCESSIVE_ANNUAL_HOURS) return 'danger';
  if (annual !== null && annual >= WATCH_ANNUAL_HOURS) return 'warning';
  if (annual === 0 && personal === 0) return 'warning';
  return 'link';
};

export const RENEWABLE_NO_EXPIRY =
  /heights|forklift|heavy vehicle|confined|hltaid|first aid|test & tag|police/i;

/* ---------------------------------------------------------------- exports */

const csvCell = (value: unknown): string => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+@]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const toCsv = (headers: string[], rows: (string | number | null | undefined)[][]) =>
  '\uFEFF' +
  [headers, ...rows]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n');

export const downloadCsv = (
  filename: string,
  headers: string[],
  rows: (string | number | null | undefined)[][]
) => {
  const blob = new Blob([toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array) => {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

/** Minimal ZIP writer (stored, no compression) for the XLSX package. */
const zipStore = (files: { name: string; data: string }[]): Blob => {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  files.forEach(({ name, data }) => {
    const nameBytes = encoder.encode(name);
    const dataBytes = encoder.encode(data);
    const crc = crc32(dataBytes);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true);
    local.setUint16(12, 0x21, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, dataBytes.length, true);
    local.setUint32(22, dataBytes.length, true);
    local.setUint16(26, nameBytes.length, true);

    parts.push(new Uint8Array(local.buffer), nameBytes, dataBytes);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true);
    entry.setUint16(4, 20, true);
    entry.setUint16(6, 20, true);
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(14, 0x21, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, dataBytes.length, true);
    entry.setUint32(24, dataBytes.length, true);
    entry.setUint16(28, nameBytes.length, true);
    entry.setUint32(42, offset, true);

    central.push(new Uint8Array(entry.buffer), nameBytes);
    offset += 30 + nameBytes.length + dataBytes.length;
  });

  const centralSize = central.reduce((total, part) => total + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...parts, ...central, new Uint8Array(end.buffer)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

const xmlEscape = (value: unknown) =>
  String(value ?? '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    .replace(/[&<>"]/g, (character) => {
      switch (character) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        default:
          return '&quot;';
      }
    });

const columnLetter = (index: number) => {
  let value = index + 1;
  let letters = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    value = Math.floor((value - 1) / 26);
  }
  return letters;
};

export const downloadXlsx = (
  filename: string,
  sheetName: string,
  headers: string[],
  rows: (string | number | null | undefined)[][]
) => {
  const namespace = 'http://schemas.openxmlformats.org/';
  const declaration = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
  const allRows = [headers, ...rows];

  const widths = headers.map((header, index) =>
    Math.min(
      60,
      Math.max(8, ...allRows.map((row) => String(row[index] ?? '').length)) + 2
    )
  );

  const cell = (value: string | number | null | undefined, rowIndex: number, colIndex: number) => {
    const reference = `${columnLetter(colIndex)}${rowIndex + 1}`;
    const style = rowIndex === 0 ? ' s="1"' : '';
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `<c r="${reference}"${style}><v>${value}</v></c>`;
    }
    return `<c r="${reference}" t="inlineStr"${style}><is><t xml:space="preserve">${xmlEscape(
      value
    )}</t></is></c>`;
  };

  const worksheet =
    `${declaration}<worksheet xmlns="${namespace}spreadsheetml/2006/main">` +
    `<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>` +
    `<cols>${widths
      .map((width, index) => `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`)
      .join('')}</cols>` +
    `<sheetData>${allRows
      .map(
        (row, rowIndex) =>
          `<row r="${rowIndex + 1}">${row
            .map((value, colIndex) => cell(value, rowIndex, colIndex))
            .join('')}</row>`
      )
      .join('')}</sheetData></worksheet>`;

  const styles =
    `${declaration}<styleSheet xmlns="${namespace}spreadsheetml/2006/main">` +
    `<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font></fonts>` +
    `<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF030213"/><bgColor indexed="64"/></patternFill></fill></fills>` +
    `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
    `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
    `<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFill="1" applyFont="1"/></cellXfs>` +
    `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;

  const safeSheet =
    xmlEscape(String(sheetName).replace(/[\[\]:*?/\\]/g, ' ').slice(0, 31)) || 'Export';

  const blob = zipStore([
    {
      name: '[Content_Types].xml',
      data:
        `${declaration}<Types xmlns="${namespace}package/2006/content-types">` +
        `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
        `<Default Extension="xml" ContentType="application/xml"/>` +
        `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
        `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
        `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`,
    },
    {
      name: '_rels/.rels',
      data:
        `${declaration}<Relationships xmlns="${namespace}package/2006/relationships">` +
        `<Relationship Id="rId1" Type="${namespace}officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      data:
        `${declaration}<workbook xmlns="${namespace}spreadsheetml/2006/main" xmlns:r="${namespace}officeDocument/2006/relationships">` +
        `<sheets><sheet name="${safeSheet}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data:
        `${declaration}<Relationships xmlns="${namespace}package/2006/relationships">` +
        `<Relationship Id="rId1" Type="${namespace}officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
        `<Relationship Id="rId2" Type="${namespace}officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    { name: 'xl/worksheets/sheet1.xml', data: worksheet },
    { name: 'xl/styles.xml', data: styles },
  ]);

  triggerDownload(blob, filename);
};

export const salaryRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '—';
  const short = (value: number) => `$${Math.round(value / 1000)}k`;
  if (min !== null && max !== null) return `${short(min)}–${short(max)}`;
  if (min !== null) return `From ${short(min)}`;
  return `Up to ${short(max as number)}`;
};
